// Reader-facing labels for the canonical nakshatra `signifies` tokens.
//
// The canonical data (packages/domain-data/src/nakshatras.ts) stores each
// signification as a snake_case id such as `success_abroad` or `vast_ocean`.
// Joining those ids straight into a sentence put raw tokens in page copy
// (e.g. "healing, mysticism, solitude, vast_ocean" on /baby-names/shatabhisha/).
// Every renderer goes through `significationLabel` instead, so a new token
// without a label fails the completeness test rather than reaching a page.

export type SignificationLanguage = "en" | "hi";

interface SignificationLabel {
  en: string;
  hi: string;
}

/** @internal */
export const NAKSHATRA_SIGNIFICATION_LABELS: Readonly<Record<string, SignificationLabel>> = {
  abundance: { en: "abundance", hi: "प्रचुरता" },
  air: { en: "air", hi: "वायु" },
  ancestors: { en: "ancestors", hi: "पितर" },
  architecture: { en: "architecture", hi: "वास्तुकला" },
  artistry: { en: "artistry", hi: "कलात्मकता" },
  authority: { en: "authority", hi: "अधिकार" },
  beauty: { en: "beauty", hi: "सौंदर्य" },
  beginnings: { en: "beginnings", hi: "नई शुरुआत" },
  clinging: { en: "clinging", hi: "आसक्ति" },
  completion: { en: "completion", hi: "पूर्णता" },
  connection: { en: "connection", hi: "जुड़ाव" },
  contracts: { en: "contracts", hi: "अनुबंध" },
  conviction: { en: "conviction", hi: "दृढ़ विश्वास" },
  cosmic_serpent: { en: "the cosmic serpent", hi: "ब्रह्मांडीय नाग" },
  craftsmanship: { en: "craftsmanship", hi: "शिल्प-कौशल" },
  creation: { en: "creation", hi: "सृजन" },
  creativity: { en: "creativity", hi: "रचनात्मकता" },
  curiosity: { en: "curiosity", hi: "जिज्ञासा" },
  cutting: { en: "cutting", hi: "तीक्ष्णता" },
  death_rebirth: { en: "death and rebirth", hi: "मृत्यु और पुनर्जन्म" },
  depth: { en: "depth", hi: "गहराई" },
  desire: { en: "desire", hi: "इच्छा" },
  destruction_creation: { en: "destruction and creation", hi: "विनाश और सृजन" },
  destruction_renewal: { en: "destruction and renewal", hi: "विनाश और नवीनीकरण" },
  determination: { en: "determination", hi: "दृढ़ संकल्प" },
  devotion: { en: "devotion", hi: "भक्ति" },
  dexterity: { en: "dexterity", hi: "हस्त-कौशल" },
  dharma: { en: "dharma", hi: "धर्म" },
  early_victory: { en: "early victory", hi: "प्रारंभिक विजय" },
  effort: { en: "effort", hi: "प्रयास" },
  endurance: { en: "endurance", hi: "सहनशक्ति" },
  fame: { en: "fame", hi: "यश" },
  fertility: { en: "fertility", hi: "उर्वरता" },
  final_victory: { en: "final victory", hi: "अंतिम विजय" },
  fire: { en: "fire", hi: "अग्नि" },
  flexibility: { en: "flexibility", hi: "लचीलापन" },
  foundation: { en: "foundation", hi: "नींव" },
  friendship: { en: "friendship", hi: "मित्रता" },
  goal: { en: "goal", hi: "लक्ष्य" },
  growth: { en: "growth", hi: "वृद्धि" },
  healing: { en: "healing", hi: "उपचार" },
  illusion: { en: "illusion", hi: "माया" },
  independence: { en: "independence", hi: "स्वतंत्रता" },
  intense_transformation: { en: "intense transformation", hi: "गहन परिवर्तन" },
  invincibility: { en: "invincibility", hi: "अजेयता" },
  kundalini: { en: "kundalini", hi: "कुंडलिनी" },
  leadership: { en: "leadership", hi: "नेतृत्व" },
  learning: { en: "learning", hi: "विद्या" },
  listening: { en: "listening", hi: "श्रवण" },
  love: { en: "love", hi: "प्रेम" },
  marriage: { en: "marriage", hi: "विवाह" },
  media: { en: "media", hi: "संचार माध्यम" },
  medicine: { en: "medicine", hi: "औषधि" },
  music: { en: "music", hi: "संगीत" },
  mysticism: { en: "mysticism", hi: "रहस्यवाद" },
  nourishment: { en: "nourishment", hi: "पोषण" },
  occult: { en: "the occult", hi: "गूढ़ विद्या" },
  organization: { en: "organization", hi: "संगठन" },
  patronage: { en: "patronage", hi: "संरक्षण" },
  penance: { en: "penance", hi: "तपस्या" },
  pleasure: { en: "pleasure", hi: "आनंद" },
  poison_medicine: { en: "poison and medicine", hi: "विष और औषधि" },
  prosperity: { en: "prosperity", hi: "समृद्धि" },
  protection: { en: "protection", hi: "रक्षा" },
  purification: { en: "purification", hi: "शुद्धि" },
  relaxation: { en: "relaxation", hi: "विश्राम" },
  renewal: { en: "renewal", hi: "नवीनीकरण" },
  research: { en: "research", hi: "अनुसंधान" },
  restoration: { en: "restoration", hi: "पुनर्स्थापना" },
  restraint: { en: "restraint", hi: "संयम" },
  return: { en: "return", hi: "पुनरागमन" },
  rhythm: { en: "rhythm", hi: "लय" },
  root_cause: { en: "the root cause", hi: "मूल कारण" },
  royalty: { en: "royalty", hi: "राजसी वैभव" },
  searching: { en: "searching", hi: "खोज" },
  seniority: { en: "seniority", hi: "वरिष्ठता" },
  skill: { en: "skill", hi: "कौशल" },
  solitude: { en: "solitude", hi: "एकांत" },
  speed: { en: "speed", hi: "गति" },
  storms: { en: "storms", hi: "तूफ़ान" },
  success_abroad: { en: "success abroad", hi: "विदेश में सफलता" },
  trade: { en: "trade", hi: "व्यापार" },
  tradition: { en: "tradition", hi: "परंपरा" },
  transformation: { en: "transformation", hi: "परिवर्तन" },
  travel: { en: "travel", hi: "यात्रा" },
  triumph: { en: "triumph", hi: "विजय" },
  universal: { en: "universality", hi: "सार्वभौमिकता" },
  vast_ocean: { en: "the vast ocean", hi: "विशाल सागर" },
  wealth: { en: "wealth", hi: "धन" },
  wisdom: { en: "wisdom", hi: "ज्ञान" },
};

/** Planet `signifies` ids that are not plain words. Single words pass through. */
const PLANET_SIGNIFICATION_LABELS: Readonly<Record<string, SignificationLabel>> = {
  past_life: { en: "past life", hi: "पूर्वजन्म" },
};

/**
 * Human label for a signification id. An unknown id still never reaches the
 * page as snake_case: underscores become spaces (the completeness test keeps
 * that fallback unused for nakshatras).
 * @internal
 */
export function significationLabel(token: string, language: SignificationLanguage = "en"): string {
  const label = NAKSHATRA_SIGNIFICATION_LABELS[token] ?? PLANET_SIGNIFICATION_LABELS[token];
  if (label) return label[language];
  return token.replace(/_/g, " ");
}

export function significationLabels(
  tokens: readonly string[],
  language: SignificationLanguage = "en"
): string[] {
  return tokens.map((token) => significationLabel(token, language));
}
